import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@clay-run/glaze/button';
import { IconButton } from '@clay-run/glaze/iconbutton';
import type { EnrollmentItem } from '@shared/flow/enrollment';
import type { ActivePlayType, PlayOption } from '@shared/flow/play-catalog';
import type { GongFlowSummary } from '@shared/flow/gong-flow-catalog';
import { canonicalizeEmail } from '@shared/normalize-email';

import { WriteRefusedError } from '../../../../lib/apiFetchJson';
import { FLOW_ICONS } from '../../icons';
import type { QueueFlashRequest } from '../../EnrollQueue/enroll-queue-types';
import { contactDisplayIdentity } from '../../EnrollQueue/contactIdentity';
import { PlayPicker } from '../../EnrollQueue/PlayPicker';
import { useContactStateDetail } from '../../contact/hooks';
import { rowMeta } from './leadDisplay';
import { RailRow } from './RailRow';

export interface UnassignedRowProps {
  item: EnrollmentItem;
  plays: PlayOption[];
  /** See `EnrollQueueContextValue.activePlayTypes`' doc comment; threaded straight into `PlayPicker` below. */
  activePlayTypes: ActivePlayType[];
  /** The picker's "Gong Flows — suggested" group; see `EnrollQueueContextValue`'s doc comment. */
  gongFlowsSuggested: GongFlowSummary[];
  readOnly: boolean;
  /** `EnrollQueueContext.assignPlay` — the SAME mutation `AssignedRow`'s own "Change Play" reassign sequence uses (D22, no second endpoint). */
  onAssign: (itemId: number, play: PlayOption) => void | Promise<void>;
  onRemove: (itemId: number) => void;
  isItemBusy: (itemId: number) => boolean;
  /** "Browse all Gong flows" enqueue-then-drill-in follow-up — read-only here; see `QueueBody.tsx`'s matching `UnassignedRow` prop doc for the full contract. */
  flashRequest?: QueueFlashRequest | null;
  requestQueueFlash?: (contactId: string, opts?: { autoOpenPicker?: boolean; openEditorAfterAssign?: boolean }) => void;
}

/**
 * The V2 rail's unassigned row (plan 2026-09-30-002, D22). Same contract as
 * `QueueBody.tsx`'s module-private `UnassignedRow` (warm-intro check via
 * `useContactStateDetail`, the browse overlay, `assignPlay`,
 * `requestQueueFlash`) — built on `RailRow` with D16's title/meta split
 * (identity alone as the title, `rowMeta(item)` as the meta — no `playName`,
 * an unassigned item has none) instead of V1's `ContactRowMeta`, and Glaze
 * `Button`/`IconButton` instead of V1's hand-rolled `<button>`s (D11).
 *
 * Unlike `LeadRow`, this row has no row-level toggle — `RailRow`'s `onToggle`
 * is left unset, matching V1: the ONLY toggle affordance is the explicit
 * `Assign to play…` button, which reveals the inline `PlayPicker` below the
 * row rather than an `expandedItemId`-style pane state (D22 doesn't add one).
 */
export function UnassignedRow({
  item,
  plays,
  activePlayTypes,
  gongFlowsSuggested,
  readOnly,
  onAssign,
  onRemove,
  isItemBusy,
  flashRequest,
  requestQueueFlash,
}: UnassignedRowProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const busy = isItemBusy(item.id);
  const name = contactDisplayIdentity(item);

  // "Browse all Gong flows" enqueue-then-drill-in follow-up — auto-reveal
  // this row's own picker the instant a flash request targeting THIS contact
  // asks for it (ported verbatim from `QueueBody.tsx`'s `UnassignedRow`).
  // `setPickerOpen(true)` is idempotent, so this is StrictMode-safe with no
  // special guarding.
  useEffect(() => {
    if (flashRequest?.contactId === item.contact_id && flashRequest.autoOpenPicker) {
      setPickerOpen(true);
    }
  }, [flashRequest, item.contact_id]);
  const autoOpenBrowse = flashRequest?.contactId === item.contact_id && !!flashRequest.autoOpenPicker;

  // Same lazy, gated-on-open warm-intro read `AssignedRow`'s reassign picker
  // and `QueueBody.tsx`'s own `UnassignedRow` both perform.
  const { data: stateDetail, isLoading: stateDetailLoading, error: stateDetailError } =
    useContactStateDetail(pickerOpen ? item.contact_id : null);
  const warmIntroAvailable =
    !pickerOpen || stateDetailLoading || !!stateDetailError || !Array.isArray(stateDetail?.facts)
      ? true
      : stateDetail.facts.some(fact => fact.dimension === 'warm_intro');

  return (
    <RailRow
      contactId={item.contact_id}
      title={name}
      meta={rowMeta(item)}
      trailing={
        <>
          {!pickerOpen && (
            <Button
              variant="secondary"
              size="xs"
              disabled={readOnly}
              title={readOnly ? 'Enroll queue is read-only while impersonating' : undefined}
              onClick={() => setPickerOpen(true)}
            >
              Assign to play…
            </Button>
          )}
          <IconButton
            variant="ghost"
            size="xs"
            icon={FLOW_ICONS.remove}
            disabled={readOnly || busy}
            title={busy ? 'Removing…' : 'Remove from queue'}
            aria-label={`Remove ${name} from queue`}
            onClick={() => onRemove(item.id)}
          />
        </>
      }
    >
      {pickerOpen && (
        <div data-no-row-toggle>
          <PlayPicker
            plays={plays}
            activePlayTypes={activePlayTypes}
            gongFlowsSuggested={gongFlowsSuggested}
            disabled={readOnly}
            disabledReason="Enroll queue is read-only while impersonating"
            ariaLabelPrefix={`Assign ${name} to`}
            contactHasEmail={canonicalizeEmail(item.contact_email) !== null}
            warmIntroAvailable={warmIntroAvailable}
            contactLabel={name}
            backLabel="← Queue"
            initialBrowseOpen={autoOpenBrowse}
            onAssign={(play, opts) => {
              const viaBrowse = !!opts?.viaBrowse;
              const contactId = item.contact_id;
              void Promise.resolve(onAssign(item.id, play)).then(
                () => {
                  if (viaBrowse) requestQueueFlash?.(contactId, { openEditorAfterAssign: true });
                },
                (err: unknown) => {
                  // apiFetch already announced a refusal.
                  if (err instanceof WriteRefusedError) return;
                  toast.error(err instanceof Error ? err.message : 'Failed to assign play');
                },
              );
              setPickerOpen(false);
            }}
          />
        </div>
      )}
    </RailRow>
  );
}
